import React from 'react'
import styled, { css, withTheme } from 'styled-components'
import { Fade } from 'react-reveal'

import Container from './Container'
import Button from './Button'
import CardsFlyout from './CardsFlyout'

import config from '../config'
import { buildCTA } from '../utils'

const Section = styled.div`
  position: relative;
  padding-bottom: 60px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    right: 0;
    bottom: 0;
    left: 0;
    background: url(${props => props.background}) left 50% repeat;
    z-index: -1;
  }

  @media (max-width: 680px) {
    padding-bottom: 90px;
  }
`

const Canvas = styled.div`
  height: 700px;
  background: transparent;
  position: relative;

  ${props =>
    props.theme.name === 'payback' &&
    css`
      height: 750px;
    `}

  @media (max-width: 1024px) {
    height: 800px;
  }

  @media (max-width: 680px) {
    height: 600px;
  }

  @media (max-width: 580px) {
    height: 500px;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  color: #fff;

  ${props =>
    props.theme.name === 'blue' &&
    css`
      color: ${config.colors.blue};
    `}

  ${props =>
    props.theme.name === 'payback' &&
    css`
      color: #4b4b4d;
    `}
`

const Background = styled.div`
  background: transparent url(${props => props.image}) center center no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  @media (max-width: 1024px) {
    ${props =>
      props.tabletImage &&
      css`
        background-image: url(${props => props.tabletImage});
      `}

    background-position: center top;
  }

  @media (max-width: 680px) {
    ${props =>
      props.mobileImage &&
      css`
        background-image: url(${props => props.mobileImage});
      `}
  }
`

const Title = styled.h1`
  margin: 0 0 40px 0;
  padding: 0;
  font-size: 50px;
  ${config.css.fontBentonBlack};
  text-transform: uppercase;
  line-height: 1.12;

  ${props =>
    props.theme.name === 'payback' &&
    css`
      color: ${config.colors.paybackBlue};
      font-family: 'PAYBACK_Regular';
      font-weight: normal;
    `}

  @media (max-width: 680px) {
    font-size: 38px;
  }

  @media (max-width: 480px) {
    font-size: 30px;
  }
`

const Subtitle = styled.div`
  font-size: 32px;
  margin: -20px 0 20px 0;
  line-height: 1.2;

  @media (max-width: 680px) {
    font-size: 26px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`

const Main = styled.div`
  padding-top: 85px;

  @media (max-width: 480px) {
    padding-top: 45px;
  }

  ${props =>
    props.theme.name === 'gold' &&
    css`
      @media (max-width: 1024px) {
        padding-top: 35px;
      }
      @media (max-width: 480px) {
        padding-top: 20px;
      }
    `}

  ${props =>
    props.theme.name === 'platinum' &&
    css`
      @media (max-width: 480px) {
        padding-top: 255px;
      }
    `}
`

const Text = styled.div`
  max-width: 440px;
  font-size: 22px;
  line-height: 1.18;

  ${props =>
    props.wide &&
    css`
      @media (min-width: 1024px) {
        max-width: 500px;
      }
    `}

  @media (max-width: 680px) {
    display: none;
  }
`

const Meta = styled.div`
  margin-top: -120px;
`

const Action = styled(Container)`
  position: relative;
  z-index: 1;
  display: block;
  margin-top: 60px;

  @media (max-width: 680px) {
    display: none;
  }
  ${props =>
    props.margin &&
    css`
      @media (min-width: 860px) and (max-width: 1200px) {
        margin-bottom: 60px;
      }
    `}

  ${props =>
    props.disableButton &&
    css`
      @media (min-width: 680px) {
        padding-bottom: 20px;
        > * {
          display: none;
        }
      }
    `}
`
const ActionMobile = styled.div`
  display: none;

  @media (max-width: 680px) {
    display: block;
  }
  ${props =>
    props.margin &&
    css`
      @media (min-width: 860px) and (max-width: 1200px) {
        margin-bottom: 60px;
      }
    `}

  ${props =>
    props.disableButton &&
    css`
      @media (min-width: 680px) {
        padding-bottom: 20px;
        > * {
          display: none;
        }
      }
    `}
`

const Cards = styled.div`
  position: relative;
  margin-right: 150px;
  transform-origin: left bottom;

  ${props =>
    props.theme.name === 'blue' &&
    css`
      opacity: 0.9;
    `}

  ${props =>
    props.cardImageSolo &&
    css`
      margin-right: 15px;
    `}

  @media (max-width: 680px) {
    transform: scale(0.8);
    margin-top: -30px;
  }

  @media (max-width: 580px) {
    transform: scale(0.6);
    margin-top: -60px;
  }

  @media (max-width: 440px) {
    transform: scale(0.45);
    margin-top: -80px;
  }
`

const CardImg = styled.img`
  display: block;
  z-index: 11;
  position: relative;
  overflow: inherit;
  /* box-shadow: 5px 0 10px rgba(0, 0, 0, 0.25); */
  filter: drop-shadow(5px 0 5px rgba(0, 0, 0, 0.4));
`

const CardFan = styled.div`
  position: absolute;
  bottom: 0;
  right: -160px;
  z-index: 0;

  ${props =>
    props.cardImageSolo &&
    css`
      display: none;
    `}
`

const CardSmallImg = styled.img`
  display: block;
  transform: scale(0.7) translate(60px, 60px);
`

const Info = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 30px;

  @media (max-width: 1100px) {
    margin-bottom: 0;
    align-items: flex-start;
    flex-direction: column;
  }
`

const InfoCards = styled.div`
  margin-right: 35px;

  ${props =>
    props.theme.name === 'platinum' &&
    css`
      margin-right: 135px;
    `}

  @media (max-width: 1024px) {
    margin-bottom: 30px;
  }
`

const InfoText = styled.div`
  /*margin-top: 160px;*/
  margin-top: 20px;
  position: relative;
  z-index: 1;
  color: ${config.colors.blue};

  ${props =>
    (props.theme.name === 'green' ||
      props.theme.name === 'blue' ||
      props.theme.name === 'platinum') &&
    css`
      color: #fff;
    `}

  ${props =>
    props.alignRight &&
    css`
      margin-left: auto;

      @media (max-width: 1024px) {
        margin-left: 0;
      }
    `}

  @media (max-width: 1024px) {
    margin-top: 0;
    margin-bottom: 30px;
  }

  @media (max-width: 680px) {
    display: none;
  }
`

const InfoTitle = styled.div`
  ${config.css.fontBentonBlack};
  color: inherit;
  font-size: 34px;
  line-height: 1.2;
  margin-bottom: 4px;
  text-transform: uppercase;
  @media (min-width: 1530px) {
    width: 115%;
  }
`

const InfoMeta = styled.div`
  color: inherit;
  font-size: 25px;
`

const MobileText = styled.div`
  color: ${config.colors.blue};
  margin-bottom: 30px;
  display: none;

  @media (max-width: 680px) {
    display: block;
    font-size: 17px;
  }
`

const BadgeImg = styled.img`
  display: block;
  position: fixed;
  top: 290px;
  right: 100px;
  z-index: 100;
  @media (max-width: 1620px) {
    right: 40px;
  }
  @media (max-width: 1440px) {
    right: 20px;
  }
  @media (max-width: 767px) {
    width: 90px;
    top: 70px;
    right: 10px;
  }
  ${props =>
    props.theme.name === 'gold' &&
    css`
      @media (max-height: 900px) and (min-width: 768px) {
        top: 150px;
      }
    `}
`

class Intro extends React.Component {
  render() {
    const {
      title,
      subtitle,
      text,
      infoTitle,
      infoMeta,
      mobileText,
      cardImage,
      cardImageSolo,
      image,
      tabletImage,
      mobileImage,
      patternImage,
      cardsFlyout,
      cardsFlyoutNumber,
      variant,
      infoMetaRight = false,
      alternativeButtonText,
      textWide = false,
      disableActionButton = false,
      badgeImg,
    } = this.props

    return (
      <Section background={patternImage.src}>
        {badgeImg && <BadgeImg src={badgeImg} alt="" width="170" />}
        <Container wide noPadding>
          <Canvas>
            <Content>
              <Container>
                <Main>
                  <Title>{title}</Title>
                  {subtitle && <Subtitle>{subtitle}</Subtitle>}
                  <Text wide={textWide}>{text}</Text>
                </Main>
              </Container>
            </Content>
            <Action
              margin={alternativeButtonText}
              disableButton={disableActionButton}>
              <Button
                href={buildCTA(variant)}
                negative={this.props.theme.name === 'green'}>
                {alternativeButtonText || `Jetzt beantragen`}
              </Button>
            </Action>
            <Background
              image={image.src}
              tabletImage={tabletImage ? tabletImage.src : null}
              mobileImage={mobileImage ? mobileImage.src : null}
            />
          </Canvas>
          <Container>
            <Meta>
              <Info>
                <InfoCards>
                  <Cards cardImageSolo={cardImageSolo}>
                    <CardImg
                      src={cardImage.src}
                      srcSet={cardImage.srcSet}
                      alt=""
                    />
                    {cardsFlyout ? (
                      <CardsFlyout
                        cards={cardsFlyout}
                        cardsFlyoutNumber={cardsFlyoutNumber}
                      />
                    ) : (
                      <Fade left delay={800} distance="180px">
                        <CardFan cardImageSolo={cardImageSolo}>
                          <CardSmallImg
                            src={cardImage.src}
                            srcSet={cardImage.srcSet}
                            alt=""
                          />
                        </CardFan>
                      </Fade>
                    )}
                  </Cards>
                </InfoCards>
                <InfoText alignRight={infoMetaRight}>
                  {infoTitle && <InfoTitle>{infoTitle}</InfoTitle>}
                  {infoMeta && <InfoMeta>{infoMeta}</InfoMeta>}
                </InfoText>
              </Info>
              <MobileText>{mobileText}</MobileText>
              <ActionMobile
                margin={alternativeButtonText}
                disableButton={disableActionButton}>
                <Button
                  href={buildCTA(variant)}
                  negative={
                    this.props.theme.name === 'green' ||
                    this.props.theme.name === 'blue'
                  }>
                  {alternativeButtonText || `Jetzt beantragen`}
                </Button>
              </ActionMobile>
            </Meta>
          </Container>
        </Container>
      </Section>
    )
  }
}

export default withTheme(Intro)
